import { Inject, Injectable } from '@angular/core';
import { ProductPricing } from '@galaxy/billing';
import { Edition as APIEdition, EditionsService } from '@galaxy/marketplace-apps/v1';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { convertPricingToTablePricing } from '../../common/table-pricing/utils';
import { BillingApiService } from '../../core/billing/billing-api.service';
import { PricingService } from '../../core/billing/pricing.service';
import { PurchaseCost } from '../../core/billing/purchase-cost';
import { DEFAULT_QUANTITY_PURCHASE_COST } from './constants';
import { AppIdToEditionIdToEditionMap, Edition, ProductEditionMap } from './interface';

export interface EditionIdentifier {
  editionId: string;
  appId: string;
}

const LIST_EDITIONS_DEFAULT_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root',
})
export class AppEditionService {
  constructor(
    private editionsService: EditionsService,
    private pricingService: PricingService,
    private billingApiService: BillingApiService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
  ) {}

  async getEditionsForApps(appIds: string[]): Promise<ProductEditionMap> {
    const productEditionMap = await firstValueFrom(this.getEditionsForAppsWithoutPricing(appIds));
    const partnerId = await firstValueFrom(this.partnerId$);
    const pricingMap = await this.extractPricingFrom(productEditionMap, partnerId);
    this.setPricingEditionFrom(productEditionMap, pricingMap);

    const pricingEditionPurchaseCostMap = await this.extractPricingEditionPurchaseCostFrom(
      productEditionMap,
      partnerId,
    );

    this.setPurchaseCostTotal(productEditionMap, pricingEditionPurchaseCostMap);

    return productEditionMap;
  }

  private async extractPricingEditionPurchaseCostFrom(
    pricingEditionMap: Map<string, Edition[]>,
    partnerId: string,
  ): Promise<Map<string, PurchaseCost>> {
    const editionsToSend = [];

    pricingEditionMap.forEach((pricingEdition: Edition[]) => {
      pricingEdition.forEach((edition) => {
        if (edition.pricing && edition.pricing.prices && edition.pricing.prices.length > 1) {
          editionsToSend.push(edition);
        }
      });
    });

    const skuCosts = await firstValueFrom(this.getMultiPurchaseCost(partnerId, editionsToSend));
    return this.getMultiPurchaseCostMapping(skuCosts);
  }

  private setPricingEditionFrom(productEditionMap: Map<string, Edition[]>, pricingMap: Map<string, ProductPricing>) {
    productEditionMap.forEach((editions: Edition[]) => {
      editions.forEach((edition) => {
        edition.pricing = convertPricingToTablePricing(pricingMap.get(edition.billingId));
      });
    });
  }

  private async extractPricingFrom(
    productEditionMap: Map<string, Edition[]>,
    partnerId: string,
  ): Promise<Map<string, ProductPricing>> {
    const billingIds = Array.from(productEditionMap.values()).reduce((ids: string[], item: Edition[]) => {
      return ids.concat(item.map((edition) => edition.billingId));
    }, []);

    let pricingDict = {};
    if (billingIds.length !== 0) {
      pricingDict = await firstValueFrom(this.pricingService.getMultiProductPricing(partnerId, billingIds));
    }
    return pricingDict ? new Map(Object.entries(pricingDict)) : new Map();
  }

  private setPurchaseCostTotal(
    productEditionMap: Map<string, Edition[]>,
    pricingEditionPurchaseCostMap: Map<string, PurchaseCost>,
  ) {
    productEditionMap.forEach((editions: Edition[]) => {
      editions.forEach((edition) => {
        const skuId = edition.editionId ? edition.billingId : edition.appId;
        const purchaseCost = pricingEditionPurchaseCostMap.get(skuId);
        if (purchaseCost) {
          edition.pricing.purchaseCostTotal = purchaseCost.totalCost;
        }
      });
    });
  }

  getEditionsForAppsWithoutPricing(appIds: string[]): Observable<ProductEditionMap> {
    const editionObservables: Observable<Edition[]>[] = [];
    for (const appId of appIds) {
      const editionsObservable = this.editionsService
        .listEditionsForApp(appId, null, LIST_EDITIONS_DEFAULT_PAGE_SIZE)
        .pipe(
          withLatestFrom(this.partnerId$),
          map(([eds]) => {
            return parseEditions(eds);
          }),
        );
      editionObservables.push(editionsObservable);
    }

    if (editionObservables.length < 1) {
      return of(new Map<string, Edition[]>());
    }

    const productEditionMap$ = forkJoin(editionObservables).pipe(
      map((editionLists) => constructEditionMapFromEditionLists(editionLists)),
    );

    return productEditionMap$;
  }

  getMultiPurchaseCost(partnerId: string, skus: Edition[]): Observable<{ id: string; cost: PurchaseCost }[]> {
    const calls: Observable<{ id: string; cost: PurchaseCost }>[] = [];
    if (skus.length > 0) {
      skus.forEach((edition) => {
        const skuId = edition.editionId ? edition.billingId : edition.appId;
        calls.push(
          this.billingApiService.getPurchaseCost(partnerId, skuId, DEFAULT_QUANTITY_PURCHASE_COST).pipe(
            map((cost) => {
              return { id: skuId, cost: cost };
            }),
          ),
        );
      });
      return forkJoin(calls);
    } else {
      return of([]);
    }
  }

  getMultiPurchaseCostMapping(editionsCosts: { id: string; cost: PurchaseCost }[]): Map<string, PurchaseCost> {
    return editionsCosts.reduce((acc, skuCost) => {
      acc.set(skuCost.id, skuCost.cost);
      return acc;
    }, new Map<string, PurchaseCost>());
  }

  getEditionsForEditionIdentifiers(identifiers: EditionIdentifier[]): Observable<AppIdToEditionIdToEditionMap> {
    if (identifiers.length <= 0) {
      return of(new Map<string, Map<string, Edition>>());
    }
    return this.editionsService.getMultiEditionForMultiApp(identifiers).pipe(
      map((sdkEditions) => parseEditions(sdkEditions)),
      map((appEditions) => constructEditionMapFromEditionList(appEditions)),
    );
  }

  getEditionsForApp(appId: string): Observable<Edition[]> {
    return this.editionsService.listEditionsForApp(appId, null, LIST_EDITIONS_DEFAULT_PAGE_SIZE).pipe(
      withLatestFrom(this.partnerId$),
      map(([eds]: [APIEdition[], string]) => {
        return parseEditions(eds);
      }),
    );
  }
}

function parseEditions(eds: any[]): Edition[] {
  if (!eds) {
    return [];
  }
  const editions = eds
    .filter((ed) => !!ed)
    .map((ed) => {
      return {
        appId: ed.appId,
        editionId: ed.editionId || '',
        editionName: ed.name || '',
        billingId: ed.billingId,
      } as Edition;
    });
  return editions;
}

/**
 * checkForEditionWithEmptyStringId returns true if any edition in a list of editions has an empty string as edition id
 * @param {Edition[]} editions - Editions to check
 * @returns {boolean}
 */
export function checkForEditionWithEmptyStringId(editions: Edition[]): boolean {
  if (!editions) {
    return false;
  }
  return editions.some((edition) => edition.editionId === '');
}

/**
 * constructEditionMapFromEditionList takes in a list of editions and builds a map of app id to a map of edition id to editions
 * @param {Edition[]} appEditions - Editions to build map from
 * @returns {AppIdToEditionIdToEditionMap}
 */
export function constructEditionMapFromEditionList(appEditions: Edition[]): AppIdToEditionIdToEditionMap {
  const editionMap = new Map<string, Map<string, Edition>>();
  if (!appEditions) {
    return editionMap;
  }
  appEditions.map((appEdition) => {
    if (appEdition) {
      const appEditionMap = editionMap.get(appEdition.appId) || new Map<string, Edition>();
      appEditionMap.set(appEdition.editionId, appEdition);
      editionMap.set(appEdition.appId, appEditionMap);
    }
  });
  return editionMap;
}

/**
 * constructEditionMapFromEditionLists takes in a list of lists of editions and builds a map of app id to editions
 * @param {Edition[][]} editionLists - List of edition lists to build map from
 * @returns {ProductEditionMap}
 */
export function constructEditionMapFromEditionLists(editionLists: Edition[][]): ProductEditionMap {
  const editionMap = new Map<string, Edition[]>() as ProductEditionMap;
  if (!editionLists) {
    return editionMap;
  }
  editionLists.map((appEditions) => {
    if (appEditions && appEditions.length > 0) {
      const appId = appEditions[0].appId;
      editionMap.set(appId, appEditions);
    }
  });
  return editionMap;
}
